import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, PieChart, Shield, BarChart3, Target, Clock, CheckCircle, ArrowRight, Wallet, Users, Award, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const MutualFundsPage = () => {
  const navigate = useNavigate();

  const fundTypes = [
    {
      title: "Equity Funds",
      description: "High growth potential with market-linked returns",
      risk: "High",
      returns: "12-15% p.a.",
      icon: TrendingUp,
      color: "from-green-500 to-emerald-600"
    },
    {
      title: "Debt Funds",
      description: "Stable returns with lower volatility",
      risk: "Low",
      returns: "6-8% p.a.",
      icon: Shield,
      color: "from-blue-500 to-cyan-600"
    },
    {
      title: "Hybrid Funds",
      description: "Balanced mix of equity and debt",
      risk: "Medium",
      returns: "9-12% p.a.",
      icon: PieChart,
      color: "from-purple-500 to-violet-600"
    },
    {
      title: "Index Funds",
      description: "Track market indices with low expense ratio",
      risk: "Medium",
      returns: "10-12% p.a.",
      icon: BarChart3,
      color: "from-orange-500 to-red-600"
    }
  ];

  const benefits = [
    { icon: Users, title: "Professional Management", desc: "Expert fund managers handle your investments" },
    { icon: PieChart, title: "Diversification", desc: "Spread risk across multiple securities" },
    { icon: Wallet, title: "Start Small", desc: "Begin investing with as low as ₹500" },
    { icon: Clock, title: "Liquidity", desc: "Easy withdrawal when you need funds" },
    { icon: Shield, title: "SEBI Regulated", desc: "Safe and transparent investment" },
    { icon: Award, title: "Tax Benefits", desc: "Save taxes with ELSS funds" }
  ];

  const steps = [
    { step: "01", title: "Choose Your Goal", desc: "Define your investment objective and timeline" },
    { step: "02", title: "Select Fund Type", desc: "Pick funds that match your risk appetite" },
    { step: "03", title: "Complete KYC", desc: "Quick digital KYC verification" },
    { step: "04", title: "Start SIP/Lumpsum", desc: "Begin your wealth creation journey" }
  ];

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 via-transparent to-green-500/10" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400/10 border border-yellow-400/30 rounded-full mb-6">
              <TrendingUp className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-sm font-medium">Smart Investment Solutions</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Mutual Funds
              <span className="block text-yellow-400">Made Simple</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Build wealth systematically with professionally managed mutual funds. 
              Start your SIP today and let your money work for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition-all shadow-lg shadow-yellow-400/25"
              >
                Start Investing <ArrowRight className="inline ml-2 w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/investment/ai-calculator')}
                className="px-8 py-4 bg-white/10 text-white font-bold rounded-xl border border-white/20 hover:bg-white/20 transition-all"
              >
                Calculate Returns
              </motion.button>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
          >
            {[
              { value: "₹40L Cr+", label: "AUM in India" },
              { value: "15Cr+", label: "Investors" },
              { value: "44+", label: "AMCs" },
              { value: "2500+", label: "Schemes" }
            ].map((stat, index) => (
              <div key={index} className="text-center p-6 bg-white/5 rounded-xl border border-white/10">
                <div className="text-2xl md:text-3xl font-bold text-yellow-400">{stat.value}</div>
                <div className="text-gray-400 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Fund Types */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Types of Mutual Funds
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Choose from a variety of fund categories based on your investment goals and risk tolerance
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {fundTypes.map((fund, index) => (
              <motion.div
                key={fund.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-yellow-500/50 transition-all cursor-pointer group"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${fund.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <fund.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{fund.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{fund.description}</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Risk: <span className={`font-medium ${fund.risk === 'High' ? 'text-red-400' : fund.risk === 'Medium' ? 'text-yellow-400' : 'text-green-400'}`}>{fund.risk}</span></span>
                  <span className="text-green-400 font-medium">{fund.returns}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Invest in Mutual Funds?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Discover the advantages that make mutual funds a preferred choice for millions of investors
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 p-6 bg-black/50 rounded-xl border border-zinc-800"
              >
                <div className="w-12 h-12 rounded-lg bg-yellow-400/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Start Investing in 4 Simple Steps
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="text-6xl font-bold text-yellow-400/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 right-0 w-1/2 h-0.5 bg-gradient-to-r from-yellow-400/50 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-yellow-400/20 to-yellow-600/10 border border-yellow-400/30 rounded-3xl p-12 text-center"
          >
            <Zap className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Start Your SIP?
            </h2>
            <p className="text-gray-300 mb-8 max-w-xl mx-auto">
              Join millions of investors who are building wealth through systematic investment. 
              Start with as little as ₹500 per month.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-4 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition-all shadow-lg shadow-yellow-400/25"
            >
              Start Your SIP Today <ArrowRight className="inline ml-2 w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MutualFundsPage;
